// ADO #582 (6) — a pasted Decision / Loop / Switch / Try-Catch used to carry
// its children over wholesale, so every nested node kept the ORIGINAL's id.
// Two nodes with one id is not a cosmetic problem: findAndDeleteNode removes
// every match, so deleting a child of the copy deleted it from the original
// too (and selecting / editing one edited both).
//
// reIdNodeTree returns a deep copy of `node` in which every nested node gets a
// fresh id, as do the containers that carry their own ids (decision / switch
// outcomes, try-catch branches). The root keeps its id: the caller (paste)
// already gives the root the id addNode made for it.

const defaultMakeId = (() => {
  let n = 0;
  return (prefix = 'node') => {
    n += 1;
    return `${prefix}-${Date.now()}-${n}-${Math.random().toString(36).slice(2, 7)}`;
  };
})();

const prefixOf = (id, fallback) => {
  const m = typeof id === 'string' && id.match(/^([a-z_]+)-/i);
  return m ? m[1] : fallback;
};

const reIdList = (nodes, makeId) => (Array.isArray(nodes)
  ? nodes.map((child) => reIdNode(child, makeId, true))
  : nodes);

function reIdNode(node, makeId, freshRoot) {
  if (!node || typeof node !== 'object') return node;
  const out = { ...node };
  if (freshRoot) out.id = makeId(prefixOf(node.id, 'node'));
  if (Array.isArray(node.outcomes)) {
    out.outcomes = node.outcomes.map((o) => ({
      ...o,
      id: makeId(prefixOf(o && o.id, 'outcome')),
      nodes: reIdList(o && o.nodes, makeId),
    }));
  }
  if (Array.isArray(node.branches)) {
    out.branches = node.branches.map((b) => ({
      ...b,
      id: makeId(prefixOf(b && b.id, 'branch')),
      nodes: reIdList(b && b.nodes, makeId),
    }));
  }
  if (Array.isArray(node.loopBody)) out.loopBody = reIdList(node.loopBody, makeId);
  if (Array.isArray(node.afterLast)) out.afterLast = reIdList(node.afterLast, makeId);
  return out;
}

export function reIdNodeTree(node, makeId = defaultMakeId) {
  const copy = JSON.parse(JSON.stringify(node));
  return reIdNode(copy, makeId, false);
}

// Every id in a node tree (root included), for tests and sanity checks.
export function collectTreeIds(node, out = []) {
  if (!node || typeof node !== 'object') return out;
  if (node.id) out.push(node.id);
  for (const o of node.outcomes || []) { if (o && o.id) out.push(o.id); for (const c of o.nodes || []) collectTreeIds(c, out); }
  for (const b of node.branches || []) { if (b && b.id) out.push(b.id); for (const c of b.nodes || []) collectTreeIds(c, out); }
  for (const c of node.loopBody || []) collectTreeIds(c, out);
  for (const c of node.afterLast || []) collectTreeIds(c, out);
  return out;
}
